import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  BookOpen,
  FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

type Step = "paste" | "extracting" | "review" | "confirm";

interface DraftRequirement {
  _key: string;
  included: boolean;
  title: string;
  feature: string;
  channel: string;
  contains_phi: boolean;
  market: string;
  sensitivity_flags: string;
  req_type: string;
  assumptions: string;
  constraints_list: string;
  raw_text: string;
}

interface Props {
  baProjectId: string;
  onConfirm: (requirements: Omit<DraftRequirement, "_key" | "included">[]) => void;
  onClose: () => void;
}

function makeKey() {
  return `k-${Date.now()}-${Math.random()}`;
}

export default function MeetingIntelWizard({ baProjectId, onConfirm, onClose }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [step, setStep] = useState<Step>("paste");
  const [transcript, setTranscript] = useState("");
  const [meetingSummary, setMeetingSummary] = useState("");
  const [drafts, setDrafts] = useState<DraftRequirement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ragContextUsed, setRagContextUsed] = useState(false);
  const [ragChunks, setRagChunks] = useState(0);

  // ─── Step 1 → 2: Extract ───────────────────────────────────────────────────

  const handleExtract = async () => {
    setStep("extracting");
    setError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/meeting-intelligence`,
        { transcript, project_id: baProjectId },
        { headers: authHeader }
      );
      setMeetingSummary(data.meeting_summary || "");
      setDrafts(
        (data.requirements || []).map((r: any) => ({
          _key: makeKey(),
          included: true,
          title: r.title || "",
          feature: r.feature || "",
          channel: r.channel || "",
          contains_phi: r.contains_phi || false,
          market: r.market || "",
          sensitivity_flags: (r.sensitivity_flags || []).join(", "),
          req_type: r.req_type || "functional",
          assumptions: (r.assumptions || []).join("\n"),
          constraints_list: (r.constraints_list || []).join("\n"),
          raw_text: r.raw_text || "",
        }))
      );
      setRagContextUsed(data.rag_context_used || false);
      setRagChunks(data.rag_chunks || 0);
      setStep("review");
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Extraction failed");
      setStep("paste");
    }
  };

  // ─── Edit helpers ──────────────────────────────────────────────────────────

  const update = (key: string, changes: Partial<DraftRequirement>) =>
    setDrafts((prev) => prev.map((d) => d._key === key ? { ...d, ...changes } : d));

  const remove = (key: string) =>
    setDrafts((prev) => prev.filter((d) => d._key !== key));

  const addBlank = () =>
    setDrafts((prev) => [...prev, {
      _key: makeKey(),
      included: true,
      title: "",
      feature: "",
      channel: "",
      contains_phi: false,
      market: "",
      sensitivity_flags: "",
      req_type: "functional",
      assumptions: "",
      constraints_list: "",
      raw_text: "",
    }]);

  // ─── Confirm ───────────────────────────────────────────────────────────────

  const handleConfirm = () => {
    const toSave = drafts
      .filter((d) => d.included && d.title.trim())
      .map(({ _key, included, ...rest }) => rest);
    onConfirm(toSave);
  };

  const includedCount = drafts.filter((d) => d.included && d.title.trim()).length;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={step === "paste" || step === "extracting" ? "text-foreground font-semibold" : ""}>
            1. Paste Notes
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "review" ? "text-foreground font-semibold" : ""}>
            2. Review Drafts
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "confirm" ? "text-foreground font-semibold" : ""}>
            3. Confirm
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">

        {/* ── STEP 1: Paste transcript ──────────────────────────────────────── */}
        {(step === "paste" || step === "extracting") && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">
                Paste Meeting Notes
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Paste a transcript from Otter.ai, your notes app, an email thread, or any text source. The AI will extract requirement drafts for your review.
              </p>
            </div>

            <Textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder={"[Otter.ai transcript, meeting notes, email thread, or any text]\n\nExample:\nJohn: We need to notify members in Florida when their claim is approved. It should go by email and include the claim amount.\nSarah: Make sure it's HIPAA compliant — we can't expose PHI in the email body.\nJohn: Right, just the claim ID and a portal link..."}
              rows={14}
              className="font-mono text-xs resize-none"
            />

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{transcript.length} characters</p>
              {error && (
                <p className="text-xs text-destructive">{error}</p>
              )}
            </div>

            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-muted-foreground max-w-sm">
                Nothing saves until you review and confirm each requirement. Your notes stay local.
              </p>
              <Button
                onClick={handleExtract}
                disabled={!transcript.trim() || step === "extracting"}
                className="gap-2 shrink-0"
              >
                {step === "extracting" ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Extracting requirements…</>
                ) : (
                  <><FileText className="h-3.5 w-3.5" /> Extract Requirements</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Review drafts ─────────────────────────────────────────── */}
        {step === "review" && (
          <div className="p-6 max-w-3xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold font-display text-foreground">
                  Review Extracted Requirements
                </h2>
                {meetingSummary && (
                  <p className="text-xs text-muted-foreground mt-1 max-w-xl">{meetingSummary}</p>
                )}
              </div>
              {ragContextUsed && (
                <div className="flex items-center gap-1 text-[11px] text-accent shrink-0 border border-accent/30 rounded px-2 py-1 bg-accent/5">
                  <BookOpen className="h-3 w-3" />
                  {ragChunks} policy chunk{ragChunks !== 1 ? "s" : ""} used
                </div>
              )}
            </div>

            <div className="space-y-4">
              {drafts.map((d, idx) => (
                <div
                  key={d._key}
                  className={`border rounded-lg bg-card transition-opacity ${!d.included ? "opacity-40" : "border-border"}`}
                >
                  {/* Card header */}
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/30 rounded-t-lg">
                    <span className="text-[11px] font-mono text-muted-foreground">Req {idx + 1}</span>
                    <div className="flex-1" />
                    <button
                      onClick={() => update(d._key, { included: !d.included })}
                      className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                        d.included
                          ? "border-primary/30 text-primary bg-primary/5 hover:bg-destructive/5 hover:text-destructive hover:border-destructive/30"
                          : "border-border text-muted-foreground hover:text-primary hover:border-primary/30"
                      }`}
                    >
                      {d.included ? "included" : "excluded"}
                    </button>
                    <button
                      onClick={() => remove(d._key)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Source excerpt */}
                    {d.raw_text && (
                      <div className="bg-secondary/40 rounded px-3 py-2 border-l-2 border-accent/40">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">From transcript</p>
                        <p className="text-xs text-muted-foreground italic">"{d.raw_text}"</p>
                      </div>
                    )}

                    {/* Title */}
                    <Input
                      value={d.title}
                      onChange={(e) => update(d._key, { title: e.target.value })}
                      placeholder="Requirement title *"
                      className="font-semibold"
                    />

                    {/* Feature + Channel */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Feature</p>
                        <Input
                          value={d.feature}
                          onChange={(e) => update(d._key, { feature: e.target.value })}
                          placeholder="e.g. claim notification"
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Channel</p>
                        <Input
                          value={d.channel}
                          onChange={(e) => update(d._key, { channel: e.target.value })}
                          placeholder="email, sms, portal…"
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>

                    {/* Market + Type */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Market / State</p>
                        <Input
                          value={d.market}
                          onChange={(e) => update(d._key, { market: e.target.value })}
                          placeholder="e.g. Florida"
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Type</p>
                        <Select value={d.req_type} onValueChange={(v) => update(d._key, { req_type: v })}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="functional">Functional</SelectItem>
                            <SelectItem value="non_functional">Non-Functional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* PHI + flags */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.contains_phi}
                          onChange={(e) => update(d._key, { contains_phi: e.target.checked })}
                          className="h-3.5 w-3.5"
                        />
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-destructive" /> Contains PHI
                        </span>
                      </label>
                      <div className="flex-1">
                        <Input
                          value={d.sensitivity_flags}
                          onChange={(e) => update(d._key, { sensitivity_flags: e.target.value })}
                          placeholder="Sensitivity flags (comma-separated)"
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addBlank}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-3 w-full transition-colors hover:border-foreground/30"
            >
              <Plus className="h-3.5 w-3.5" /> Add a requirement manually
            </button>

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                {includedCount} of {drafts.length} requirements included
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep("paste")}>Back</Button>
                <Button
                  size="sm"
                  onClick={() => setStep("confirm")}
                  disabled={includedCount === 0}
                  className="gap-1.5"
                >
                  Review & Confirm <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Confirm ───────────────────────────────────────────────── */}
        {step === "confirm" && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">
                Confirm & Add to Requirements
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {includedCount} requirement{includedCount !== 1 ? "s" : ""} will be added to your project.
              </p>
            </div>

            <div className="space-y-2">
              {drafts
                .filter((d) => d.included && d.title.trim())
                .map((d) => (
                  <div key={d._key} className="border border-border rounded-lg bg-card px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-card-foreground">{d.title}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {[d.req_type, d.feature, d.channel, d.market].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                      <Badge variant="outline" className="text-[10px] px-1.5">{d.req_type}</Badge>
                      {d.contains_phi && (
                        <Badge variant="outline" className="text-[10px] px-1.5 bg-destructive/10 text-destructive border-destructive/30 gap-0.5">
                          <AlertTriangle className="h-2.5 w-2.5" /> PHI
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            <div className="border border-border rounded-lg bg-secondary/20 px-4 py-3 text-xs text-muted-foreground space-y-0.5">
              <p className="font-semibold text-foreground">What happens next</p>
              <p>· Requirements are saved to your project's requirements list.</p>
              <p>· You can run Impact Analysis or Generate Stories on any of them.</p>
              <p>· This action is logged in the Agent Log tab.</p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" size="sm" onClick={() => setStep("review")}>Back to Review</Button>
              <Button onClick={handleConfirm} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Add {includedCount} requirement{includedCount !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
